'use client';

import Link from 'next/link';
import Image from 'next/image';
import {
  Badge,
  Box,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import type { Locale } from '../../../i18n/locales';
import { getCarComparisonLinkProps } from '../../../lib/content/comparisonLinks';

interface HeroBlockProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  trustLabel?: string;
  accentTitle?: string;
  backgroundImage?: string;
  locale?: Locale;
  variant?: 'home' | 'page';
}

function HeroActionRow({
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
}: {
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
}) {
  return (
    <Group gap="xs" wrap="wrap">
      {primaryCtaLabel && primaryCtaHref ? (
        <Button
          component={Link}
          href={primaryCtaHref}
          {...getCarComparisonLinkProps(primaryCtaHref)}
          rightSection={<IconArrowRight size={16} />}
          styles={{ root: { background: 'var(--brand-primary)', color: '#fff' } }}
        >
          {primaryCtaLabel}
        </Button>
      ) : null}

      {secondaryCtaLabel && secondaryCtaHref ? (
        <Button
          component={Link}
          href={secondaryCtaHref}
          {...getCarComparisonLinkProps(secondaryCtaHref)}
          variant="default"
          styles={{
            root: {
              background: 'var(--surface-soft)',
              borderColor: 'var(--surface-border)',
              color: 'var(--text-primary)',
            },
          }}
        >
          {secondaryCtaLabel}
        </Button>
      ) : null}
    </Group>
  );
}

export function HeroBlock({
  title,
  subtitle,
  eyebrow,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  trustLabel,
  accentTitle,
  backgroundImage,
  variant = 'page',
}: HeroBlockProps) {
  const plainSubtitle = subtitle
    ? subtitle
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';
  const resolvedTrustLabel = trustLabel?.trim();
  const normalizedBackgroundImage = backgroundImage?.trim();
  const localBackgroundImage = normalizedBackgroundImage?.startsWith('/') ? normalizedBackgroundImage : undefined;
  const safeBackgroundImage = normalizedBackgroundImage?.replace(/"/g, '\\"');

  if (variant === 'home') {
    return (
      <Box py={{ base: 48, md: 80 }}>
        <Container size="xl">
          <Stack gap={24} align="center">
            {eyebrow ? (
              <Badge
                variant="light"
                size="lg"
                styles={{ root: { background: 'var(--surface-soft)', color: 'var(--text-primary)', border: '1px solid var(--surface-border)' } }}
              >
                {eyebrow}
              </Badge>
            ) : null}

            <Title
              order={1}
              ta="center"
              style={{
                color: 'var(--text-primary)',
                fontWeight: 500,
                lineHeight: 1.2,
                letterSpacing: '-0.03em',
                fontSize: 'clamp(2.4rem, 5.2vw, 4rem)',
                maxWidth: 760,
              }}
            >
              {title}
              {accentTitle ? (
                <>
                  {' '}
                  <Text component="span" inherit c="logo.6">
                    {accentTitle}
                  </Text>
                </>
              ) : null}
            </Title>

            {plainSubtitle ? (
              <Text ta="center" style={{ color: 'var(--text-secondary)', maxWidth: 760, fontSize: 16 }}>
                {plainSubtitle}
              </Text>
            ) : null}

            <HeroActionRow
              primaryCtaLabel={primaryCtaLabel}
              primaryCtaHref={primaryCtaHref}
              secondaryCtaLabel={secondaryCtaLabel}
              secondaryCtaHref={secondaryCtaHref}
            />

            <Box
              mt={26}
              w="100%"
              maw={820}
              style={{
                position: 'relative',
                aspectRatio: '1.45 / 1',
              }}
            >
              <Box
                style={{
                  position: 'absolute',
                  inset: '8% 17% 10% 17%',
                  borderRadius: '50%',
                  background: 'var(--surface-soft)',
                  border: '1px solid var(--surface-border)',
                }}
              />
              <Paper
                p={10}
                radius={40}
                withBorder
                style={{
                  position: 'absolute',
                  inset: '14% 24% 14% 24%',
                  overflow: 'hidden',
                  borderColor: 'var(--surface-border)',
                  background: '#fff',
                }}
              >
                <Box
                  h="100%"
                  style={{
                    borderRadius: 30,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {localBackgroundImage ? (
                    <Image
                      src={localBackgroundImage}
                      alt={title}
                      fill
                      priority
                      sizes="(max-width: 768px) 66vw, 420px"
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      h="100%"
                      style={{
                      backgroundImage: safeBackgroundImage
                        ? `url("${safeBackgroundImage}")`
                          : 'none',
                        backgroundColor: safeBackgroundImage ? undefined : 'var(--surface-alt)',
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                  )}
                </Box>
              </Paper>

            </Box>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box py={{ base: 52, md: 80 }}>
      <Container size="xl">
        <SimpleGrid cols={{ base: 1, md: backgroundImage ? 2 : 1 }} spacing={{ base: 28, md: 60 }}>
          <Stack gap={16} justify="center" maw={760}>
            {eyebrow ? (
              <Badge
                variant="light"
                w="fit-content"
                styles={{ root: { background: 'var(--surface-soft)', color: 'var(--text-primary)', border: '1px solid var(--surface-border)' } }}
              >
                {eyebrow}
              </Badge>
            ) : null}

            <Title
              order={1}
              style={{
                color: 'var(--text-primary)',
                fontWeight: 500,
                lineHeight: 1.2,
                letterSpacing: '-0.03em',
                fontSize: 'clamp(2.1rem, 4.8vw, 4rem)',
                maxWidth: 820,
              }}
            >
              {title}
              {accentTitle ? (
                <>
                  {' '}
                  <Text component="span" inherit c="logo.6">
                    {accentTitle}
                  </Text>
                </>
              ) : null}
            </Title>

            {plainSubtitle ? (
              <Text style={{ color: 'var(--text-secondary)', maxWidth: 700, fontSize: 16 }}>
                {plainSubtitle}
              </Text>
            ) : null}

            <HeroActionRow
              primaryCtaLabel={primaryCtaLabel}
              primaryCtaHref={primaryCtaHref}
              secondaryCtaLabel={secondaryCtaLabel}
              secondaryCtaHref={secondaryCtaHref}
            />

            {resolvedTrustLabel ? (
              <Text size="sm" fw={500} style={{ color: 'var(--text-muted)' }}>
                {resolvedTrustLabel}
              </Text>
            ) : null}
          </Stack>

          {backgroundImage ? (
            <Paper
              p={12}
              radius={40}
              withBorder
              style={{
                borderColor: 'var(--surface-border)',
                background: '#fff',
                overflow: 'hidden',
                minHeight: 360,
              }}
            >
              <Box
                h="100%"
                style={{
                  borderRadius: 30,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {localBackgroundImage ? (
                  <Image
                    src={localBackgroundImage}
                    alt={title}
                    fill
                    sizes="(max-width: 768px) 100vw, 42vw"
                    style={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    h="100%"
                    style={{
                      backgroundImage: safeBackgroundImage
                        ? `url("${safeBackgroundImage}")`
                        : 'none',
                      backgroundColor: safeBackgroundImage ? undefined : 'var(--surface-alt)',
                      backgroundPosition: 'center',
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                )}
              </Box>
            </Paper>
          ) : null}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
